import { useEffect, useRef, useState } from 'react';
import { Box, Button, Text, useColorModeValue } from '@chakra-ui/react';
import { copyToClipboard } from '@/helpers/clipboard';

export const CONTACT_EMAIL = 'sam@tenteams.co.uk';

type FooterContactProps = {
  linkColor: string;
};

/**
 * Footer contact: closed shows "Contact"; open reveals the email.
 * Clicking the email copies it and shows a short "Copied" note above.
 * Email stays in the DOM when closed (aria-hidden, clipped) so fetchers can still find it.
 */
export function FooterContact({ linkColor }: FooterContactProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const emailButtonRef = useRef<HTMLButtonElement>(null);
  const copiedTimeoutRef = useRef<number | null>(null);
  const tipBg = useColorModeValue('gray.800', 'gray.200');
  const tipColor = useColorModeValue('white', 'gray.900');

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current != null) {
        window.clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  async function handleCopy() {
    try {
      await copyToClipboard(CONTACT_EMAIL);
      setCopied(true);
      if (copiedTimeoutRef.current != null) {
        window.clearTimeout(copiedTimeoutRef.current);
      }
      copiedTimeoutRef.current = window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Keep quiet in the footer; user can still select/copy manually if clipboard fails.
      setCopied(false);
    }
  }

  function handleOpen() {
    setOpen(true);
    requestAnimationFrame(() => {
      emailButtonRef.current?.focus();
    });
  }

  return (
    <Box as='span' display='inline-flex' alignItems='center' position='relative'>
      {!open ? (
        <Box
          as='span'
          aria-hidden='true'
          position='absolute'
          w='1px'
          h='1px'
          p={0}
          m='-1px'
          overflow='hidden'
          whiteSpace='nowrap'
          border={0}
          sx={{ clip: 'rect(0, 0, 0, 0)' }}
        >
          Contact: {CONTACT_EMAIL}
        </Box>
      ) : null}

      {!open ? (
        <Button
          variant='link'
          fontSize='sm'
          fontWeight='normal'
          color={linkColor}
          textDecoration='none'
          height='auto'
          minW='unset'
          p={0}
          aria-expanded={false}
          aria-controls='footer-contact-email'
          _hover={{ textDecoration: 'underline' }}
          onClick={handleOpen}
        >
          Contact
        </Button>
      ) : (
        <Box id='footer-contact-email' as='span' position='relative' display='inline-flex'>
          {copied ? (
            <Text
              as='span'
              position='absolute'
              left='50%'
              bottom='100%'
              mb={1}
              transform='translateX(-50%)'
              whiteSpace='nowrap'
              fontSize='xs'
              fontWeight='medium'
              lineHeight='1.2'
              px={2}
              py={1}
              borderRadius='md'
              bg={tipBg}
              color={tipColor}
              pointerEvents='none'
              zIndex={1}
              aria-hidden='true'
            >
              Copied
            </Text>
          ) : null}
          <Button
            ref={emailButtonRef}
            variant='link'
            fontSize='sm'
            fontWeight='normal'
            color={linkColor}
            textDecoration='none'
            height='auto'
            minW='unset'
            p={0}
            aria-label={`Copy ${CONTACT_EMAIL}`}
            _hover={{ textDecoration: 'underline' }}
            onClick={() => void handleCopy()}
          >
            {CONTACT_EMAIL}
          </Button>
          <Box as='span' position='absolute' w={0} h={0} overflow='hidden' aria-live='polite'>
            {copied ? 'Email address copied to clipboard' : null}
          </Box>
        </Box>
      )}
    </Box>
  );
}
